import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box } from '@atlaskit/primitives';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import { token } from '@atlaskit/tokens';

import {
	ReactionContainerType,
	ReactionLocation,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { ReactionsLoader } from '@confluence/reactions/entry-points/ReactionsLoader.tsx';

const i18n = defineMessages({
	addNew: {
		id: 'live-pages-features.byline.reactions.add.new',
		description: 'Message displayed in button to add a new reaction.',
		defaultMessage: 'Add new',
	},
	addReaction: {
		id: 'live-pages-features.byline.reactions.add.reaction',
		description: 'Message displayed to add a reaction.',
		defaultMessage: 'Add reaction',
	},
});

export type LivePagesByLineReactionsProps = {
	contentId: string;
	spaceId: string;
	isDisabled?: boolean;
	contentType?: string;
};

const floatingContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'left',
	transition: 'left 600ms',
	marginBottom: 'space.025',
	height: '28px',
	marginTop: 'space.025',
});

export const LivePagesByLineReactions = ({
	contentId,
	spaceId,
	isDisabled = false,
	contentType,
}: LivePagesByLineReactionsProps) => {
	const { formatMessage } = useIntl();
	const isLivePage = useIsCurrentPageLive();

	// Used when we want to allow the user to select reactions from the summary view popup
	const customIcon = (
		<EmojiAddIcon
			testId="emoji-add-icon"
			color={token('color.icon.subtle')}
			label={formatMessage(i18n.addReaction)}
		/>
	);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	return (
		<Box xcss={floatingContainerStyles} testId="live-pages-reactions">
			<ReactionsLoader
				contentId={contentId}
				location={contentType === 'blogpost' ? ReactionLocation.BLOGPOST : ReactionLocation.PAGE}
				containerId={spaceId}
				containerType={ReactionContainerType.SPACE}
				contentSubType={isLivePage ? LIVE_PAGE_SUBTYPE : null}
				summaryViewEnabled
				summaryViewThreshold={1}
				allowSelectFromSummaryView
				useButtonAlignmentStyling
				summaryViewPlacement="bottom-start"
				hideNoReactionsText
				readOnly={isDisabled}
				isViewOnly={isDisabled}
				isSubtle
				reactionPickerTriggerIcon={customIcon}
				reactionPickerTriggerText={formatMessage(i18n.addNew)}
				useDefaultStyledContainer
				hidePlaceholder={isSSR}
				noRelativeContainer
				noFlexWrap
				hoverableSummaryView
				optimisticallyLoadSummaryEmojis={isSSR}
				renderParticleEffectOnSummaryView
			/>
		</Box>
	);
};
